// frontend/src/pages/AdminContactInbox.js
// Build: 2026-07-11-support-mail-desk-unified-search-v2

import React, { useEffect, useMemo, useState } from "react";
import "../App.css";
import "./AdminContactInbox.css";
import { api } from "../api";

const INBOX_PAGE_LIMIT = 40;
const SENT_PAGE_LIMIT = 40;
const COMPOSE_ATTACHMENT_MAX_FILES = 3;
const COMPOSE_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;
const COMPOSE_ATTACHMENT_ALLOWED_EXTENSIONS = new Set([
  "pdf",
  "doc",
  "docx",
  "rtf",
  "txt",
  "jpg",
  "jpeg",
  "png",
  "webp",
]);

function clean(value) {
  return typeof value === "string" ? value.trim() : "";
}

function idOf(value) {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    return String(value._id || value.id || value.$oid || value.toString?.() || "");
  }
  return String(value);
}

function formatDate(value) {
  try {
    if (!value) return "";
    return new Date(value).toLocaleString();
  } catch {
    return "";
  }
}

function formatBytes(bytes) {
  const value = Number(bytes || 0);
  if (!Number.isFinite(value) || value <= 0) return "0 B";
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

function extensionOf(filename) {
  const parts = String(filename || "").toLowerCase().split(".");
  return parts.length > 1 ? parts.pop() : "";
}

function isAllowedAttachment(file) {
  return COMPOSE_ATTACHMENT_ALLOWED_EXTENSIONS.has(extensionOf(file?.name));
}

function fileIdentity(file) {
  return [file?.name || "", file?.size || 0, file?.lastModified || 0].join("::");
}

function mergeUniqueById(existing, incoming) {
  const seen = new Set((existing || []).map((item) => idOf(item?._id)));
  const output = [...(existing || [])];

  for (const item of incoming || []) {
    const id = idOf(item?._id);
    if (!id || seen.has(id)) continue;
    seen.add(id);
    output.push(item);
  }

  return output;
}

function prependUniqueById(existing, item) {
  const id = idOf(item?._id);
  if (!id) return existing;
  return [item, ...(existing || []).filter((entry) => idOf(entry?._id) !== id)];
}

function defaultReplySubject(message) {
  const subject = clean(message?.subject) || "Your message to NextScenes";
  return /^re:/i.test(subject) ? subject : `Re: ${subject}`;
}

function replyCountFor(message) {
  const count = Number(message?.replyCount);
  if (Number.isFinite(count)) return count;
  return Array.isArray(message?.replies) ? message.replies.length : 0;
}

function listText(value) {
  if (Array.isArray(value)) return value.filter(Boolean).join(", ");
  return clean(value);
}

function sourceLabel(source) {
  return source === "contact_reply" ? "Contact reply" : "New support email";
}

async function postMultipart(path, formData) {
  return api(path, {
    method: "POST",
    body: formData,
  });
}

async function deleteRequest(path) {
  return api(path, { method: "DELETE" });
}

function DeskButton({
  children,
  onClick,
  disabled = false,
  active = false,
  tone = "outline",
  type = "button",
  className = "",
}) {
  const resolvedTone = active ? "primary" : tone;

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`ns-button support-desk-button support-desk-button-${resolvedTone} ${className}`.trim()}
    >
      {children}
    </button>
  );
}

function Notice({ notice, onClose }) {
  if (!notice?.text) return null;

  return (
    <div className={`support-desk-notice support-desk-notice-${notice.type || "info"}`} role="status">
      <div>{notice.text}</div>
      {onClose ? (
        <button type="button" onClick={onClose} aria-label="Dismiss message">
          ×
        </button>
      ) : null}
    </div>
  );
}

function StatusBadge({ status }) {
  const normalized = clean(status).toLowerCase() || "new";
  return <span className={`support-desk-status support-desk-status-${normalized}`}>{normalized}</span>;
}

function ConfirmDialog({ dialog, onCancel, onConfirm }) {
  useEffect(() => {
    if (!dialog) return undefined;

    function handleKeyDown(event) {
      if (event.key === "Escape") onCancel();
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [dialog, onCancel]);

  if (!dialog) return null;

  return (
    <div className="support-desk-modal-backdrop" role="presentation">
      <div
        className="support-desk-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="support-desk-dialog-title"
      >
        <div className="support-desk-modal-mark" aria-hidden="true">
          {dialog.tone === "danger" ? "!" : "✓"}
        </div>

        <h2 id="support-desk-dialog-title">{dialog.title}</h2>
        {dialog.description ? <p>{dialog.description}</p> : null}

        {Array.isArray(dialog.rows) && dialog.rows.length ? (
          <dl className="support-desk-confirm-rows">
            {dialog.rows.map((row) => (
              <React.Fragment key={`${row.label}-${row.value}`}>
                <dt>{row.label}</dt>
                <dd>{row.value || "None"}</dd>
              </React.Fragment>
            ))}
          </dl>
        ) : null}

        {dialog.warning ? (
          <div className={`support-desk-modal-warning support-desk-modal-warning-${dialog.tone || "info"}`}>
            {dialog.warning}
          </div>
        ) : null}

        <div className="support-desk-modal-actions">
          <DeskButton onClick={onCancel}>Cancel</DeskButton>
          <DeskButton tone={dialog.tone === "danger" ? "danger" : "primary"} onClick={onConfirm}>
            {dialog.confirmLabel || "Confirm"}
          </DeskButton>
        </div>
      </div>
    </div>
  );
}

function SearchBar({
  label,
  draft,
  setDraft,
  applied,
  onSearch,
  onClear,
  disabled,
  placeholder,
}) {
  return (
    <div className="support-desk-search-panel">
      <div className="support-desk-search-label">{label}</div>
      <input
        className="ns-input support-desk-search-input"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            onSearch();
          }
        }}
        placeholder={placeholder}
        disabled={disabled}
      />
      <div className="support-desk-search-actions">
        <DeskButton tone="primary" onClick={onSearch} disabled={disabled}>
          Search
        </DeskButton>
        <DeskButton onClick={onClear} disabled={disabled}>
          Clear
        </DeskButton>
      </div>
      {applied ? (
        <div className="support-desk-search-result">
          Showing results for <strong>{applied}</strong>
        </div>
      ) : null}
    </div>
  );
}

export default function AdminContactInbox() {
  const [view, setView] = useState("inbox");
  const [pageNotice, setPageNotice] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);

  const [filter, setFilter] = useState("new");
  const [searchDraft, setSearchDraft] = useState("");
  const [search, setSearch] = useState("");
  const [items, setItems] = useState([]);
  const [nextBefore, setNextBefore] = useState("");
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [err, setErr] = useState("");

  const [sentItems, setSentItems] = useState([]);
  const [sentNextBefore, setSentNextBefore] = useState("");
  const [sentHasMore, setSentHasMore] = useState(false);
  const [sentLoading, setSentLoading] = useState(false);
  const [sentLoadingMore, setSentLoadingMore] = useState(false);
  const [sentErr, setSentErr] = useState("");
  const [openSentId, setOpenSentId] = useState("");

  const [openReplyId, setOpenReplyId] = useState("");
  const [replyDrafts, setReplyDrafts] = useState({});
  const [replySendingId, setReplySendingId] = useState("");
  const [replyNotice, setReplyNotice] = useState({});

  const [composeOpen, setComposeOpen] = useState(false);
  const [composeDraft, setComposeDraft] = useState({
    to: "",
    cc: "",
    subject: "",
    intro: "Thank you for contacting NextScenes.",
    message: "",
  });
  const [composeAttachments, setComposeAttachments] = useState([]);
  const [composeSending, setComposeSending] = useState(false);
  const [composeNotice, setComposeNotice] = useState(null);

  const inboxQueryString = useMemo(() => {
    const params = new URLSearchParams();
    params.set("limit", String(INBOX_PAGE_LIMIT));

    if (clean(search)) params.set("q", clean(search));

    if (filter === "archived") {
      params.set("archived", "1");
    } else if (filter !== "all") {
      params.set("status", filter);
    }

    return `?${params.toString()}`;
  }, [filter, search]);

  const sentQueryString = useMemo(() => {
    const params = new URLSearchParams();
    params.set("limit", String(SENT_PAGE_LIMIT));
    if (clean(search)) params.set("q", clean(search));
    return `?${params.toString()}`;
  }, [search]);

  function showError(error, fallback) {
    setPageNotice({
      type: "error",
      text: error?.data?.error || error?.message || fallback,
    });
  }

  function openConfirmation(dialog) {
    setConfirmDialog(dialog);
  }

  function handleConfirm() {
    const action = confirmDialog?.onConfirm;
    setConfirmDialog(null);
    if (typeof action === "function") action();
  }

  function patchComposeDraft(patch) {
    setComposeDraft((previous) => ({ ...previous, ...patch }));
    if (composeNotice?.type === "success") setComposeNotice(null);
  }

  function addComposeAttachments(fileList) {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;

    let warning = "";

    setComposeAttachments((previous) => {
      const seen = new Set(previous.map(fileIdentity));
      const next = [...previous];

      for (const file of incoming) {
        if (next.length >= COMPOSE_ATTACHMENT_MAX_FILES) {
          warning = `You may attach up to ${COMPOSE_ATTACHMENT_MAX_FILES} files.`;
          break;
        }

        if (!isAllowedAttachment(file)) {
          warning = "Only PDF, Word, text, JPG, PNG, and WebP attachments are allowed.";
          continue;
        }

        if (Number(file?.size || 0) > COMPOSE_ATTACHMENT_MAX_BYTES) {
          warning = "Each attachment must be 10 MB or smaller.";
          continue;
        }

        const key = fileIdentity(file);
        if (seen.has(key)) continue;

        seen.add(key);
        next.push(file);
      }

      return next;
    });

    setComposeNotice(warning ? { type: "error", text: warning } : null);
  }

  function removeComposeAttachment(index) {
    setComposeAttachments((previous) => previous.filter((_, itemIndex) => itemIndex !== index));
    setComposeNotice(null);
  }

  async function performSendComposedEmail() {
    const to = clean(composeDraft.to);
    const subject = clean(composeDraft.subject);
    const message = clean(composeDraft.message);

    setComposeSending(true);
    setComposeNotice(null);

    try {
      const formData = new FormData();
      formData.append("to", to);
      formData.append("cc", composeDraft.cc || "");
      formData.append("subject", subject);
      formData.append("intro", composeDraft.intro || "");
      formData.append("message", message);

      composeAttachments.forEach((file) => {
        formData.append("attachments", file, file.name);
      });

      const data = await postMultipart("/api/admin/contact/support-email", formData);

      if (data?.item) {
        setSentItems((previous) => prependUniqueById(previous, data.item));
      }

      setComposeDraft({
        to: "",
        cc: "",
        subject: "",
        intro: "Thank you for contacting NextScenes.",
        message: "",
      });
      setComposeAttachments([]);
      setComposeNotice({
        type: "success",
        text: "Email sent successfully and saved in Sent Mail.",
      });
      setPageNotice({
        type: "success",
        text: "The support email was sent and its copy is available in Sent Mail.",
      });
    } catch (error) {
      setComposeNotice({
        type: "error",
        text: error?.data?.error || error?.message || "Support email could not be sent.",
      });
    } finally {
      setComposeSending(false);
    }
  }

  function requestSendComposedEmail() {
    const to = clean(composeDraft.to);
    const subject = clean(composeDraft.subject);
    const message = clean(composeDraft.message);

    if (!to) {
      setComposeNotice({ type: "error", text: "Please enter at least one recipient email address." });
      return;
    }
    if (!subject) {
      setComposeNotice({ type: "error", text: "Please enter a subject." });
      return;
    }
    if (!message) {
      setComposeNotice({ type: "error", text: "Please write the email message before sending." });
      return;
    }

    setComposeNotice(null);
    openConfirmation({
      title: "Send support email?",
      description: "Please confirm the details before the message leaves NextScenes.",
      rows: [
        { label: "From", value: "NextScenes Support <support@nextscenes.org>" },
        { label: "To", value: to },
        { label: "CC", value: clean(composeDraft.cc) || "None" },
        { label: "Subject", value: subject },
        {
          label: "Attachments",
          value: composeAttachments.length
            ? `${composeAttachments.length} file${composeAttachments.length === 1 ? "" : "s"}`
            : "None",
        },
      ],
      warning: "This email will be sent immediately and saved in Sent Mail.",
      confirmLabel: "Send email",
      tone: "primary",
      onConfirm: performSendComposedEmail,
    });
  }

  function patchReplyDraft(id, patch) {
    setReplyDrafts((previous) => ({
      ...previous,
      [id]: {
        ...(previous[id] || {}),
        ...patch,
      },
    }));

    setReplyNotice((previous) => ({ ...previous, [id]: null }));
  }

  function ensureReplyDraft(message) {
    const id = idOf(message?._id);
    if (!id) return;

    setReplyDrafts((previous) => {
      if (previous[id]) return previous;
      return {
        ...previous,
        [id]: {
          subject: defaultReplySubject(message),
          message: "",
        },
      };
    });
  }

  function toggleReplyBox(message) {
    const id = idOf(message?._id);
    if (!id) return;
    ensureReplyDraft(message);
    setOpenReplyId((current) => (current === id ? "" : id));
    setReplyNotice((previous) => ({ ...previous, [id]: null }));
  }

  async function performSendReply(message) {
    const id = idOf(message?._id);
    const draft = replyDrafts[id] || {};
    const subject = clean(draft.subject) || defaultReplySubject(message);
    const body = clean(draft.message);

    setReplySendingId(id);
    setReplyNotice((previous) => ({ ...previous, [id]: null }));

    try {
      const data = await api.post(`/api/admin/contact/${id}/reply`, {
        subject,
        message: body,
      });

      if (data?.item) {
        setItems((previous) =>
          previous.map((item) => (idOf(item?._id) === id ? { ...item, ...data.item } : item))
        );
      }

      if (data?.supportEmail) {
        setSentItems((previous) => prependUniqueById(previous, data.supportEmail));
      }

      setReplyDrafts((previous) => ({
        ...previous,
        [id]: {
          subject: defaultReplySubject(data?.item || message),
          message: "",
        },
      }));
      setReplyNotice((previous) => ({
        ...previous,
        [id]: { type: "success", text: "Reply sent and saved in Sent Mail." },
      }));
      setOpenReplyId("");
      setPageNotice({ type: "success", text: "The branded reply was sent and saved in Sent Mail." });
    } catch (error) {
      setReplyNotice((previous) => ({
        ...previous,
        [id]: {
          type: "error",
          text: error?.data?.error || error?.message || "Reply could not be sent.",
        },
      }));
    } finally {
      setReplySendingId("");
    }
  }

  function requestSendReply(message) {
    const id = idOf(message?._id);
    const draft = replyDrafts[id] || {};
    const subject = clean(draft.subject) || defaultReplySubject(message);
    const body = clean(draft.message);

    if (!body) {
      setReplyNotice((previous) => ({
        ...previous,
        [id]: { type: "error", text: "Please write a reply before sending." },
      }));
      return;
    }

    openConfirmation({
      title: "Send support reply?",
      description: "Please confirm the reply before it is sent.",
      rows: [
        { label: "From", value: "NextScenes Support <support@nextscenes.org>" },
        { label: "To", value: message?.email || "Unknown recipient" },
        { label: "Subject", value: subject },
      ],
      warning: "This reply will be sent immediately and saved in Sent Mail.",
      confirmLabel: "Send reply",
      tone: "primary",
      onConfirm: () => performSendReply(message),
    });
  }

  async function loadFirstPage() {
    setLoading(true);
    setErr("");

    try {
      const data = await api.get(`/api/admin/contact${inboxQueryString}`);
      const list = Array.isArray(data?.items) ? data.items : [];
      const before = clean(data?.nextBefore);
      setItems(list);
      setNextBefore(before);
      setHasMore(Boolean(before) && list.length >= INBOX_PAGE_LIMIT);
    } catch (error) {
      setErr(error?.data?.error || error?.message || "Could not load contact messages.");
      setItems([]);
      setNextBefore("");
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }

  async function loadMore() {
    if (!hasMore || !nextBefore) return;

    setLoadingMore(true);
    setErr("");

    try {
      const data = await api.get(
        `/api/admin/contact${inboxQueryString}&before=${encodeURIComponent(nextBefore)}`
      );
      const list = Array.isArray(data?.items) ? data.items : [];
      const before = clean(data?.nextBefore);
      setItems((previous) => mergeUniqueById(previous, list));
      setNextBefore(before);
      setHasMore(Boolean(before) && list.length >= INBOX_PAGE_LIMIT);
    } catch (error) {
      setErr(error?.data?.error || error?.message || "Could not load more messages.");
    } finally {
      setLoadingMore(false);
    }
  }

  async function loadSentFirstPage() {
    setSentLoading(true);
    setSentErr("");

    try {
      const data = await api.get(`/api/admin/contact/support-email/sent${sentQueryString}`);
      const list = Array.isArray(data?.items) ? data.items : [];
      const before = clean(data?.nextBefore);
      setSentItems(list);
      setSentNextBefore(before);
      setSentHasMore(Boolean(before) && list.length >= SENT_PAGE_LIMIT);
    } catch (error) {
      setSentErr(error?.data?.error || error?.message || "Could not load Sent Mail.");
      setSentItems([]);
      setSentNextBefore("");
      setSentHasMore(false);
    } finally {
      setSentLoading(false);
    }
  }

  async function loadMoreSent() {
    if (!sentHasMore || !sentNextBefore) return;

    setSentLoadingMore(true);
    setSentErr("");

    try {
      const data = await api.get(
        `/api/admin/contact/support-email/sent${sentQueryString}&before=${encodeURIComponent(
          sentNextBefore
        )}`
      );
      const list = Array.isArray(data?.items) ? data.items : [];
      const before = clean(data?.nextBefore);
      setSentItems((previous) => mergeUniqueById(previous, list));
      setSentNextBefore(before);
      setSentHasMore(Boolean(before) && list.length >= SENT_PAGE_LIMIT);
    } catch (error) {
      setSentErr(error?.data?.error || error?.message || "Could not load more sent emails.");
    } finally {
      setSentLoadingMore(false);
    }
  }

  async function setMessageStatus(id, nextStatus) {
    try {
      const data = await api.patch(`/api/admin/contact/${id}`, { status: nextStatus });
      if (!data?.item) return;

      setItems((previous) =>
        previous.map((item) =>
          idOf(item?._id) === idOf(id) ? { ...item, ...data.item } : item
        )
      );
    } catch (error) {
      showError(error, "The message status could not be updated.");
    }
  }

  async function archiveMessage(id) {
    try {
      const data = await api.patch(`/api/admin/contact/${id}/archive`, {});
      if (!data?.item) return;

      if (filter === "archived") {
        setItems((previous) =>
          previous.map((item) =>
            idOf(item?._id) === idOf(id) ? { ...item, ...data.item } : item
          )
        );
      } else {
        setItems((previous) => previous.filter((item) => idOf(item?._id) !== idOf(id)));
      }

      setPageNotice({ type: "success", text: "The message was archived." });
    } catch (error) {
      showError(error, "The message could not be archived.");
    }
  }

  async function unarchiveMessage(id) {
    try {
      const data = await api.patch(`/api/admin/contact/${id}/unarchive`, {});
      if (!data?.item) return;

      if (filter === "archived") {
        setItems((previous) => previous.filter((item) => idOf(item?._id) !== idOf(id)));
      } else {
        setItems((previous) =>
          previous.map((item) =>
            idOf(item?._id) === idOf(id) ? { ...item, ...data.item } : item
          )
        );
      }

      setPageNotice({ type: "success", text: "The message was returned to the inbox." });
    } catch (error) {
      showError(error, "The message could not be restored.");
    }
  }

  async function performDeleteMessage(message) {
    const id = idOf(message?._id);

    try {
      const data = await deleteRequest(`/api/admin/contact/${id}`);
      setItems((previous) => previous.filter((item) => idOf(item?._id) !== id));
      setSentItems((previous) =>
        previous.filter((item) => idOf(item?.relatedContactMessageId) !== id)
      );
      setOpenReplyId((current) => (current === id ? "" : current));

      const relatedCount = Number(data?.relatedSentRecordsDeleted || 0);
      setPageNotice({
        type: "success",
        text: relatedCount
          ? `The contact message and ${relatedCount} linked sent reply record${
              relatedCount === 1 ? "" : "s"
            } were deleted permanently.`
          : "The contact message was deleted permanently.",
      });
    } catch (error) {
      showError(error, "The contact message could not be deleted.");
    }
  }

  function requestDeleteMessage(message) {
    openConfirmation({
      title: "Delete this contact message permanently?",
      description: "This action is intended for test messages, spam, or records that should not remain in the inbox.",
      rows: [
        { label: "Subject", value: message?.subject || "No subject" },
        {
          label: "Sender",
          value: `${message?.name || "Unknown"} <${message?.email || "no-email"}>`,
        },
        { label: "Received", value: formatDate(message?.createdAt) || "Unknown" },
      ],
      warning:
        "The message and any sent reply records linked to it will be removed. This cannot be undone.",
      confirmLabel: "Delete permanently",
      tone: "danger",
      onConfirm: () => performDeleteMessage(message),
    });
  }

  async function performDeleteSentEmail(email) {
    const id = idOf(email?._id);

    try {
      const data = await deleteRequest(`/api/admin/contact/support-email/sent/${id}`);
      setSentItems((previous) => previous.filter((item) => idOf(item?._id) !== id));
      setOpenSentId((current) => (current === id ? "" : current));
      setPageNotice({
        type: "success",
        text:
          data?.note ||
          "The Sent Mail record was deleted. An email already delivered to its recipient cannot be recalled.",
      });
    } catch (error) {
      showError(error, "The Sent Mail record could not be deleted.");
    }
  }

  function requestDeleteSentEmail(email) {
    openConfirmation({
      title: "Delete this Sent Mail record?",
      description: "This removes the stored NextScenes copy from the Support Mail Desk.",
      rows: [
        { label: "To", value: listText(email?.to) || "Unknown recipient" },
        { label: "Subject", value: email?.subject || "No subject" },
        { label: "Sent", value: formatDate(email?.sentAt || email?.createdAt) || "Unknown" },
      ],
      warning:
        "Deleting this record will not recall or remove the email from the recipient's mailbox.",
      confirmLabel: "Delete record",
      tone: "danger",
      onConfirm: () => performDeleteSentEmail(email),
    });
  }

  function applySearch() {
    const term = clean(searchDraft);
    setFilter("all");
    setSearch(term);
    setPageNotice(
      term
        ? {
            type: "info",
            text: "Searching both Inbox and Sent Mail. Matching records are shown under their respective tabs.",
          }
        : null
    );
  }

  function clearSearch() {
    setSearchDraft("");
    setSearch("");
    setPageNotice(null);
  }

  useEffect(() => {
    loadFirstPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inboxQueryString]);

  useEffect(() => {
    if (view === "sent" || clean(search)) loadSentFirstPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, sentQueryString]);

  useEffect(() => {
    if (!clean(search) || loading || sentLoading) return;

    if (view === "inbox" && items.length === 0 && sentItems.length > 0) {
      setView("sent");
      return;
    }

    if (view === "sent" && sentItems.length === 0 && items.length > 0) {
      setView("inbox");
    }
  }, [search, loading, sentLoading, items.length, sentItems.length, view]);

  return (
    <div className="ns-page support-desk-page">
      <ConfirmDialog
        dialog={confirmDialog}
        onCancel={() => setConfirmDialog(null)}
        onConfirm={handleConfirm}
      />

      <div className="ns-page-head support-desk-page-head">
        <div>
          <h1 className="ns-h1">Support Mail Desk</h1>
          <p className="ns-subtitle">
            Manage public contact messages, send branded support emails, and review copies of outgoing mail.
          </p>
        </div>

        <DeskButton
          tone="primary"
          onClick={() => {
            setComposeOpen((current) => !current);
            setComposeNotice(null);
          }}
          disabled={composeSending}
        >
          {composeOpen ? "Hide composer" : "Compose support email"}
        </DeskButton>
      </div>

      <Notice notice={pageNotice} onClose={() => setPageNotice(null)} />

      <div className="support-desk-main-tabs" role="tablist" aria-label="Support Mail Desk sections">
        <DeskButton active={view === "inbox"} onClick={() => setView("inbox")}>
          Inbox{clean(search) ? ` (${items.length}${hasMore ? "+" : ""})` : ""}
        </DeskButton>
        <DeskButton active={view === "sent"} onClick={() => setView("sent")}>
          Sent Mail{clean(search) ? ` (${sentItems.length}${sentHasMore ? "+" : ""})` : ""}
        </DeskButton>
      </div>

      <SearchBar
        label="Search all support mail"
        draft={searchDraft}
        setDraft={setSearchDraft}
        applied={search}
        onSearch={applySearch}
        onClear={clearSearch}
        disabled={loading || loadingMore || sentLoading || sentLoadingMore}
        placeholder="name, sender, recipient, subject, message, attachment…"
      />

      {composeOpen ? (
        <section className="ns-paper support-desk-compose-panel" aria-label="Compose support email">
          <div className="support-desk-section-heading">
            <div>
              <h2 className="ns-h2">Compose branded support email</h2>
              <p className="ns-p">
                Send a new message from NextScenes Support. A searchable copy will be saved in Sent Mail.
              </p>
            </div>
          </div>

          <div className="support-desk-form-grid support-desk-form-grid-two">
            <label>
              <span>To</span>
              <input
                className="ns-input"
                value={composeDraft.to}
                onChange={(event) => patchComposeDraft({ to: event.target.value })}
                disabled={composeSending}
                placeholder="recipient@example.com"
              />
            </label>

            <label>
              <span>CC, optional</span>
              <input
                className="ns-input"
                value={composeDraft.cc}
                onChange={(event) => patchComposeDraft({ cc: event.target.value })}
                disabled={composeSending}
                placeholder="copy@example.com"
              />
            </label>
          </div>

          <div className="support-desk-form-grid">
            <label>
              <span>Subject</span>
              <input
                className="ns-input"
                value={composeDraft.subject}
                onChange={(event) => patchComposeDraft({ subject: event.target.value })}
                disabled={composeSending}
                placeholder="Message from NextScenes"
              />
            </label>

            <label>
              <span>Intro line</span>
              <input
                className="ns-input"
                value={composeDraft.intro}
                onChange={(event) => patchComposeDraft({ intro: event.target.value })}
                disabled={composeSending}
                placeholder="Thank you for contacting NextScenes."
              />
            </label>

            <label>
              <span>Email message</span>
              <textarea
                className="ns-input support-desk-textarea"
                rows={9}
                value={composeDraft.message}
                onChange={(event) => patchComposeDraft({ message: event.target.value })}
                disabled={composeSending}
                placeholder="Write the email message that should be sent with the branded NextScenes template…"
              />
            </label>

            <label>
              <span>Attachments, optional</span>
              <input
                className="ns-input"
                type="file"
                multiple
                accept=".pdf,.doc,.docx,.rtf,.txt,.jpg,.jpeg,.png,.webp"
                disabled={composeSending || composeAttachments.length >= COMPOSE_ATTACHMENT_MAX_FILES}
                onChange={(event) => {
                  addComposeAttachments(event.target.files);
                  event.target.value = "";
                }}
              />
              <small>
                Up to {COMPOSE_ATTACHMENT_MAX_FILES} files. PDF, Word, text, JPG, PNG, or WebP. Maximum 10 MB per file.
              </small>
            </label>
          </div>

          {composeAttachments.length ? (
            <div className="support-desk-attachment-list">
              {composeAttachments.map((file, index) => (
                <div className="support-desk-attachment" key={fileIdentity(file)}>
                  <div>
                    <strong>{file.name}</strong>
                    <span>{formatBytes(file.size)}</span>
                  </div>
                  <DeskButton onClick={() => removeComposeAttachment(index)} disabled={composeSending}>
                    Remove
                  </DeskButton>
                </div>
              ))}
            </div>
          ) : null}

          <Notice notice={composeNotice} onClose={() => setComposeNotice(null)} />

          <div className="support-desk-form-actions">
            <DeskButton tone="primary" onClick={requestSendComposedEmail} disabled={composeSending}>
              {composeSending ? "Sending…" : "Send branded email"}
            </DeskButton>
            <DeskButton onClick={() => setComposeOpen(false)} disabled={composeSending}>
              Close
            </DeskButton>
          </div>
        </section>
      ) : null}

      {view === "inbox" ? (
        <section className="support-desk-view" role="tabpanel">
          <div className="support-desk-view-toolbar">
            <div className="support-desk-filter-row">
              <DeskButton active={filter === "all"} onClick={() => setFilter("all")} disabled={loading}>
                All
              </DeskButton>
              <DeskButton active={filter === "new"} onClick={() => setFilter("new")} disabled={loading}>
                New
              </DeskButton>
              <DeskButton
                active={filter === "reviewed"}
                onClick={() => setFilter("reviewed")}
                disabled={loading}
              >
                Reviewed
              </DeskButton>
              <DeskButton
                active={filter === "closed"}
                onClick={() => setFilter("closed")}
                disabled={loading}
              >
                Closed
              </DeskButton>
              <DeskButton
                active={filter === "archived"}
                onClick={() => setFilter("archived")}
                disabled={loading}
              >
                Archived
              </DeskButton>
            </div>

            <DeskButton onClick={loadFirstPage} disabled={loading || loadingMore}>
              {loading ? "Refreshing…" : "Refresh"}
            </DeskButton>
          </div>

          {err ? <Notice notice={{ type: "error", text: err }} /> : null}
          {loading ? <div className="support-desk-empty">Loading contact messages…</div> : null}

          {!loading && items.length === 0 ? (
            <div className="support-desk-empty">
              {clean(search) && sentItems.length > 0
                ? `No Inbox matches. ${sentItems.length}${sentHasMore ? "+" : ""} matching record${sentItems.length === 1 && !sentHasMore ? " is" : "s are"} available in Sent Mail.`
                : "No contact messages were found."}
            </div>
          ) : null}

          {!loading
            ? items.map((message) => {
                const id = idOf(message?._id);
                const archived = Boolean(message?.archivedAt);
                const replyDraft = replyDrafts[id] || {
                  subject: defaultReplySubject(message),
                  message: "",
                };
                const replyOpen = openReplyId === id;
                const sendingReply = replySendingId === id;
                const replyCount = replyCountFor(message);
                const messageReplyNotice = replyNotice[id];

                return (
                  <article className="ns-paper support-desk-card" key={id}>
                    <div className="support-desk-card-head">
                      <div className="support-desk-card-title-block">
                        <div className="support-desk-card-title-row">
                          <h2 className="ns-h2">{message?.subject || "(No subject)"}</h2>
                          <StatusBadge status={message?.status} />
                        </div>
                        <div className="support-desk-sender">
                          <strong>{message?.name || "Unknown"}</strong>
                          <span>&lt;{message?.email || "no-email"}&gt;</span>
                        </div>
                        <div className="support-desk-meta-row">
                          <span>Received {formatDate(message?.createdAt) || "at an unknown time"}</span>
                          {archived ? <span>Archived {formatDate(message?.archivedAt)}</span> : null}
                          {replyCount > 0 ? (
                            <span>
                              {replyCount} repl{replyCount === 1 ? "y" : "ies"}
                              {message?.lastRepliedAt
                                ? ` · Last reply ${formatDate(message.lastRepliedAt)}`
                                : ""}
                            </span>
                          ) : null}
                        </div>
                        {message?.pageUrl ? (
                          <a
                            className="support-desk-page-link"
                            href={message.pageUrl}
                            target="_blank"
                            rel="noreferrer"
                          >
                            Open submitted page
                          </a>
                        ) : null}
                      </div>

                      <div className="support-desk-card-actions">
                        {!archived ? (
                          <>
                            <DeskButton tone="primary" onClick={() => toggleReplyBox(message)} disabled={sendingReply}>
                              {replyOpen ? "Hide reply" : "Reply"}
                            </DeskButton>
                            {message?.status !== "reviewed" ? (
                              <DeskButton onClick={() => setMessageStatus(id, "reviewed")}>
                                Mark reviewed
                              </DeskButton>
                            ) : null}
                            {message?.status !== "closed" ? (
                              <DeskButton onClick={() => setMessageStatus(id, "closed")}>Close</DeskButton>
                            ) : null}
                            {message?.status !== "new" ? (
                              <DeskButton onClick={() => setMessageStatus(id, "new")}>Reopen</DeskButton>
                            ) : null}
                            <DeskButton onClick={() => archiveMessage(id)}>Archive</DeskButton>
                          </>
                        ) : (
                          <DeskButton onClick={() => unarchiveMessage(id)}>Unarchive</DeskButton>
                        )}
                        <DeskButton tone="danger" onClick={() => requestDeleteMessage(message)}>
                          Delete
                        </DeskButton>
                      </div>
                    </div>

                    <div className="support-desk-message-body">{message?.message || ""}</div>

                    {messageReplyNotice ? <Notice notice={messageReplyNotice} /> : null}

                    {replyOpen ? (
                      <div className="support-desk-reply-panel">
                        <div>
                          <h3>Send branded support reply</h3>
                          <p>The reply will be sent from NextScenes Support and saved in Sent Mail.</p>
                        </div>

                        <div className="support-desk-form-grid">
                          <label>
                            <span>To</span>
                            <input className="ns-input" value={message?.email || ""} disabled />
                          </label>
                          <label>
                            <span>Subject</span>
                            <input
                              className="ns-input"
                              value={replyDraft.subject}
                              onChange={(event) => patchReplyDraft(id, { subject: event.target.value })}
                              disabled={sendingReply}
                            />
                          </label>
                          <label>
                            <span>Reply message</span>
                            <textarea
                              className="ns-input support-desk-textarea"
                              rows={7}
                              value={replyDraft.message}
                              onChange={(event) => patchReplyDraft(id, { message: event.target.value })}
                              disabled={sendingReply}
                              placeholder="Write the reply that should be sent to this sender…"
                            />
                          </label>
                        </div>

                        <div className="support-desk-form-actions">
                          <DeskButton tone="primary" onClick={() => requestSendReply(message)} disabled={sendingReply}>
                            {sendingReply ? "Sending…" : "Send branded reply"}
                          </DeskButton>
                          <DeskButton onClick={() => setOpenReplyId("")} disabled={sendingReply}>
                            Cancel
                          </DeskButton>
                        </div>
                      </div>
                    ) : null}

                    {message?.ip || message?.userAgent ? (
                      <details className="support-desk-technical-details">
                        <summary>Technical submission details</summary>
                        {message?.ip ? <div>IP: {message.ip}</div> : null}
                        {message?.userAgent ? <div>User agent: {message.userAgent}</div> : null}
                      </details>
                    ) : null}
                  </article>
                );
              })
            : null}

          {!loading && items.length > 0 ? (
            <div className="support-desk-load-more">
              {hasMore ? (
                <DeskButton onClick={loadMore} disabled={loadingMore}>
                  {loadingMore ? "Loading more…" : "Load more"}
                </DeskButton>
              ) : (
                <span>End of results.</span>
              )}
            </div>
          ) : null}
        </section>
      ) : (
        <section className="support-desk-view" role="tabpanel">
          <div className="support-desk-sent-intro">
            <div>
              <h2 className="ns-h2">Sent Mail</h2>
              <p className="ns-p">
                These are NextScenes records of outgoing support emails and replies. Attachment names and sizes are retained, but the attachment files themselves are not stored here.
              </p>
            </div>
            <DeskButton onClick={loadSentFirstPage} disabled={sentLoading || sentLoadingMore}>
              {sentLoading ? "Refreshing…" : "Refresh"}
            </DeskButton>
          </div>

          {sentErr ? <Notice notice={{ type: "error", text: sentErr }} /> : null}
          {sentLoading ? <div className="support-desk-empty">Loading Sent Mail…</div> : null}

          {!sentLoading && sentItems.length === 0 ? (
            <div className="support-desk-empty">
              {clean(search) && items.length > 0
                ? `No Sent Mail matches. ${items.length}${hasMore ? "+" : ""} matching record${items.length === 1 && !hasMore ? " is" : "s are"} available in Inbox.`
                : "No sent email records were found."}
            </div>
          ) : null}

          {!sentLoading
            ? sentItems.map((email) => {
                const id = idOf(email?._id);
                const expanded = openSentId === id;
                const attachments = Array.isArray(email?.attachments) ? email.attachments : [];

                return (
                  <article className="ns-paper support-desk-card support-desk-sent-card" key={id}>
                    <div className="support-desk-card-head">
                      <div className="support-desk-card-title-block">
                        <div className="support-desk-card-title-row">
                          <h2 className="ns-h2">{email?.subject || "(No subject)"}</h2>
                          <StatusBadge status={email?.status || "sent"} />
                        </div>
                        <div className="support-desk-sent-recipient">
                          <strong>To:</strong> {listText(email?.to) || "Unknown recipient"}
                        </div>
                        {listText(email?.cc) ? (
                          <div className="support-desk-sent-recipient">
                            <strong>CC:</strong> {listText(email.cc)}
                          </div>
                        ) : null}
                        <div className="support-desk-meta-row">
                          <span>{sourceLabel(email?.source)}</span>
                          <span>Sent {formatDate(email?.sentAt || email?.createdAt) || "at an unknown time"}</span>
                          {email?.sentByName || email?.sentByEmail ? (
                            <span>
                              Sent by {email.sentByName || email.sentByEmail}
                              {email.sentByName && email.sentByEmail ? ` <${email.sentByEmail}>` : ""}
                            </span>
                          ) : null}
                        </div>
                      </div>

                      <div className="support-desk-card-actions">
                        <DeskButton tone="primary" onClick={() => setOpenSentId(expanded ? "" : id)}>
                          {expanded ? "Hide email" : "View email"}
                        </DeskButton>
                        <DeskButton tone="danger" onClick={() => requestDeleteSentEmail(email)}>
                          Delete record
                        </DeskButton>
                      </div>
                    </div>

                    {attachments.length ? (
                      <div className="support-desk-sent-attachments">
                        <strong>Attachments:</strong>
                        {attachments.map((attachment, index) => (
                          <span key={`${attachment?.filename || "attachment"}-${index}`}>
                            {attachment?.filename || "attachment"}
                            {attachment?.size ? ` (${formatBytes(attachment.size)})` : ""}
                          </span>
                        ))}
                      </div>
                    ) : null}

                    {expanded ? (
                      <div className="support-desk-sent-copy">
                        {email?.intro ? (
                          <div className="support-desk-sent-intro-line">{email.intro}</div>
                        ) : null}
                        <div className="support-desk-message-body">{email?.message || ""}</div>
                        {email?.status === "failed" && email?.error ? (
                          <div className="support-desk-failure-detail">Failure: {email.error}</div>
                        ) : null}
                      </div>
                    ) : null}
                  </article>
                );
              })
            : null}

          {!sentLoading && sentItems.length > 0 ? (
            <div className="support-desk-load-more">
              {sentHasMore ? (
                <DeskButton onClick={loadMoreSent} disabled={sentLoadingMore}>
                  {sentLoadingMore ? "Loading more…" : "Load more"}
                </DeskButton>
              ) : (
                <span>End of Sent Mail.</span>
              )}
            </div>
          ) : null}
        </section>
      )}
    </div>
  );
}
